import { motion } from "framer-motion";
import { useContext } from "react";
import { Link } from "react-router-dom";
import { ThemeContext } from "../../hooks/ThemeContext";
import { Palette, ShoppingBag, Users, ShieldCheck } from "lucide-react";

const HeroSection = ({ totalCreations = 0 }) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const formattedCreations =
    typeof totalCreations === "number" && totalCreations > 0
      ? totalCreations.toLocaleString("fr-FR")
      : "-";

  return (
    <section className="relative w-full py-16 sm:py-20">
      <div
        className={`relative overflow-hidden border shadow-xl ${
          isDark
            ? "border-white/10 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20"
            : "bg-gradient-to-br from-rose-50 via-white to-orange-50"
        }`}
      >
        <div className={`absolute -top-24 -right-20 h-64 w-64 rounded-full opacity-30 blur-3xl ${isDark ? "bg-rose-500/40" : "bg-rose-200"}`} />
        <div className={`absolute bottom-[-40px] left-[-20px] h-60 w-60 rounded-full opacity-30 blur-3xl ${isDark ? "bg-purple-500/30" : "bg-purple-200"}`} />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center px-8 py-14"
        >
          <div className="space-y-6">
            <div
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.2em] ${
                isDark ? "bg-white/5 text-white/70 border border-white/10 shadow-xl" : "bg-white text-gray-600 border border-rose-100"
              }`}
            >
              <Palette className="w-4 h-4" />
              Studio digital
            </div>

            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] ${isDark ? "text-white" : "text-gray-900"}`}>
              Vendez vos creations.
              <span className={`block ${isDark ? "text-rose-400" : "text-rose-600"}`}>
                Achetez l'identite parfaite.
              </span>
            </h1>

            <p className={`text-lg leading-8 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Krayne Agency connecte clients et createurs avec des fichiers premium, des livraisons rapides et un support sous 24h.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/shop"
                onClick={() => window.scrollTo(0, 0)}
                className={`inline-flex items-center gap-3 px-6 py-3 font-semibold shadow-xl transition hover:scale-[1.02] ${
                  isDark ? "bg-rose-500 text-white" : "bg-rose-600 text-white"
                }`}
              >
                <ShoppingBag className="w-5 h-5" />
                Explorer la boutique
              </Link>
              <Link
                to="/devenir-createur"
                onClick={() => window.scrollTo(0, 0)}
                className={`inline-flex items-center gap-3 px-6 py-3 font-semibold transition shadow-xl hover:scale-[1.02] ${
                  isDark
                    ? "border border-white/20 bg-white/5 text-white hover:bg-white/10"
                    : "border border-gray-900 text-gray-900"
                }`}
              >
                <Users className="w-5 h-5" />
                Devenir créateur
              </Link>
            </div>

            <div
              className={`grid grid-cols-3 gap-4 rounded-2xl p-4 ${
                isDark ? "bg-white/5 border border-white/10 shadow-xl" : "bg-white/90"
              }`}
            >
              {[
                { label: "Creations", value: formattedCreations },
                { label: "Support", value: "24h" },
                { label: "Paiement", value: "Securise" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className={`text-2xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{stat.value}</div>
                  <div className={`text-xs uppercase tracking-[0.15em] ${isDark ? "text-white/50" : "text-gray-500"}`}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative mt-6 lg:mt-0">
            <div
              className={`p-6 shadow-xl border ${
                isDark ? "bg-white/5 border-white/10" : "bg-white/95 border-rose-100"
              }`}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className={`text-xs uppercase tracking-[0.2em] ${isDark ? "text-white/50" : "text-gray-500"}`}>
                    Ce que vous obtenez
                  </div>
                  <div className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                    Une experience fluide
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs ${isDark ? "bg-white/10 text-white/70" : "bg-rose-50 text-rose-700"}`}>
                  Verifie
                </div>
              </div>

              <div className="space-y-4">
                {[
                  {
                    title: "Livraison immediate",
                    desc: "Acces aux fichiers des paiement valide.",
                    icon: ShoppingBag,
                  },
                  {
                    title: "Paiements securises",
                    desc: "Protection et suivi des commandes inclus.",
                    icon: ShieldCheck,
                  },
                  {
                    title: "Support reactif",
                    desc: "Equipe disponible sous 24h.",
                    icon: Users,
                  },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className={`flex items-center gap-4 px-4 py-3 border shadow-xl ${
                          isDark ? "bg-white/5 border-white/10" : "bg-rose-50 border-rose-100"
                      }`}
                    >
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${isDark ? "bg-white/10" : "bg-white"}`}>
                        <Icon className={`w-5 h-5 ${isDark ? "text-white" : "text-rose-600"}`} />
                      </div>
                      <div>
                        <div className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{item.title}</div>
                        <div className={`text-xs ${isDark ? "text-white/60" : "text-gray-600"}`}>{item.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
